import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { adminBenefitsAPI, adminStoresAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const BenefitsManagement = () => {
  const [benefits, setBenefits] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    storeId: '',
    title: '',
    description: '',
    validUntil: '',
    isActive: true,
    onePerUser: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [benefitsRes, storesRes] = await Promise.all([
        adminBenefitsAPI.getAll(),
        adminStoresAPI.getAll(),
      ]);
      setBenefits(benefitsRes.data);
      setStores(storesRes.data.filter(s => s.isParticipating));
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los datos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (benefit) => {
    setSelectedBenefit(benefit);
    setFormData({
      storeId: benefit.storeId,
      title: benefit.title,
      description: benefit.description,
      validUntil: benefit.validUntil || '',
      isActive: benefit.isActive !== undefined ? benefit.isActive : true,
      onePerUser: benefit.onePerUser !== undefined ? benefit.onePerUser : false,
    });
  };

  const handleSave = async () => {
    try {
      if (selectedBenefit) {
        const response = await adminBenefitsAPI.update(selectedBenefit.id, formData);
        setBenefits(benefits.map(b => 
          b.id === selectedBenefit.id ? response.data : b
        ));
      } else {
        const response = await adminBenefitsAPI.create(formData);
        setBenefits([...benefits, response.data]);
      }
      setSelectedBenefit(null);
      setIsAdding(false);
      toast({
        title: "Beneficio guardado",
        description: "El beneficio ha sido actualizado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar el beneficio",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (benefitId) => {
    try {
      await adminBenefitsAPI.delete(benefitId);
      setBenefits(benefits.filter(b => b.id !== benefitId));
      toast({
        title: "Beneficio eliminado",
        description: "El beneficio ha sido eliminado",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el beneficio",
        variant: "destructive"
      });
    }
  };

  const handleAdd = () => {
    setFormData({
      storeId: '',
      title: '',
      description: '',
      validUntil: '',
      isActive: true,
      onePerUser: false,
    });
    setIsAdding(true);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <p className="text-white">Cargando beneficios...</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-white mb-2">Gestión de beneficios</h2>
              <p className="text-[#A2A2A2]">Crea y administra beneficios para las tiendas</p>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-white text-black hover:bg-white/90 rounded-full px-6 py-3 transition-all hover:scale-105 active:scale-95"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear beneficio
            </Button>
          </div>

          {/* Benefits List */}
          <div className="space-y-3">
            {benefits.map((benefit) => (
              <div
                key={benefit.id}
                className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/20 transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl font-semibold text-white">{benefit.storeName}</h3>
                      {benefit.isNew && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white">
                          Nuevo
                        </span>
                      )}
                      {benefit.isActive ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                          Activo
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-300 border border-red-500/30">
                          Inactivo
                        </span>
                      )}
                      {benefit.onePerUser ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                          Una vez por cliente
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-300 border border-gray-500/30">
                          Ilimitado
                        </span>
                      )}
                    </div>
                    <p className="text-white/80 mb-1">{benefit.title}</p>
                    <p className="text-[#A2A2A2] mb-2">{benefit.description}</p>
                    <p className="text-[#A2A2A2] text-sm">Válido hasta: {benefit.validUntil}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => handleEdit(benefit)}
                      variant="outline"
                      className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(benefit.id)}
                      variant="outline"
                      className="border-white/10 text-white bg-transparent hover:bg-red-500/10 rounded-full"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add/Edit Benefit Dialog */}
      <Dialog open={!!selectedBenefit || isAdding} onOpenChange={() => { setSelectedBenefit(null); setIsAdding(false); }}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {selectedBenefit ? 'Editar beneficio' : 'Crear beneficio'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label className="text-white">Tienda</Label>
              <Select value={formData.storeId} onValueChange={(value) => setFormData({ ...formData, storeId: value })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-2xl h-12">
                  <SelectValue placeholder="Selecciona una tienda" />
                </SelectTrigger>
                <SelectContent className="bg-black border-white/20 text-white">
                  {stores.map((store) => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-white">Título corto</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                placeholder="Ej: Solo este fin"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Descripción del beneficio</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
                placeholder="Ej: Hasta 30% adicional hoy"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Vigencia hasta</Label>
              <Input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
                className="bg-white/5 border-white/10 text-white rounded-2xl h-12"
              />
            </div>
            
            <div className="space-y-4 pt-2 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label className="text-white">Estado</Label>
                  <p className="text-[#A2A2A2] text-sm">Activo / Inactivo</p>
                </div>
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Tipo de uso</Label>
                <Select 
                  value={formData.onePerUser ? 'once' : 'unlimited'} 
                  onValueChange={(value) => setFormData({ ...formData, onePerUser: value === 'once' })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-2xl h-12">
                    <SelectValue placeholder="Selecciona el tipo de uso" />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/20 text-white">
                    <SelectItem value="unlimited">Ilimitado</SelectItem>
                    <SelectItem value="once">Una vez por cliente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button
              onClick={handleSave}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95"
            >
              {selectedBenefit ? 'Guardar cambios' : 'Crear beneficio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Toaster />
    </>
  );
};

export default BenefitsManagement;
